import submit from "submit-form";
import i18n from "../i18n";
import getErrorMessage from "./getErrorMessage";
import { toUtcNumber } from "./nutrition-progress";

const loadRegex = /^[0-9]+\.(?:\d{1,5})$|^[0-9]+$/;
const wholeNumberRegex = /^0*[1-9]\d*$/;

// The 6 goal types AddGoal.js's modal actually renders a form for. goalType
// comes from a URL query param (modalState) that can go missing (e.g. the
// modal gets reopened after an error without it) — without this list,
// goalValidation had no `else` branch and fell through every `if` straight
// to `return true`, silently approving a submit with no type/details at all.
export const VALID_GOAL_TYPES = [
  "steps",
  "sleep",
  "waterIntake",
  "bodyWeight",
  "personalRecord",
  "custom",
];

// `utcTargetDate` is a separate param, not a `details` key - bodyWeight/
// personalRecord's `details` schema (msb-api) has no such field (it's stored
// at the top level of the goal document, alongside `details`), so folding it
// in here would just leak an extra, unpersisted-anywhere-else key into what
// actually gets saved.
export const goalValidation = (
  goalType,
  details,
  assert,
  flash,
  utcTargetDate
) => {
  try {
    assert(
      VALID_GOAL_TYPES.includes(goalType),
      400,
      i18n.t("common:validation.goalTypeRequired")
    );

    const {
      customName,
      customTarget,
      customIcon,
      targetSteps,
      targetWeight,
      targetSleep,
      targetWaterIntake,
      targetName,
      targetLoad,
      targetReps,
    } = details;

    // Only bodyWeight/personalRecord are ever placed on the Periodization
    // timeline (see resolveMilestones) - a past target date doesn't error
    // anywhere on save, it just silently drops out of
    // resolveActiveBodyweightGoal/resolveMilestones' own date filters, so the
    // goal saves fine but never shows up again (no chip, no milestone). The
    // DateInput's own `min` prop only guards the calendar UI, not typed-in
    // text, so this is the one place that actually enforces it.
    if (
      (goalType === "bodyWeight" || goalType === "personalRecord") &&
      utcTargetDate != null
    ) {
      assert(
        utcTargetDate >= toUtcNumber(new Date()),
        400,
        i18n.t("common:validation.targetDateMustBeFuture")
      );
    }

    if (goalType === "bodyWeight") {
      assert(targetWeight, 400, i18n.t("common:validation.weightRequired"));
      assert(
        loadRegex.test(targetWeight),
        400,
        i18n.t("common:validation.weightMustBeNumber")
      );
    }
    if (goalType === "steps") {
      assert(targetSteps, 400, i18n.t("common:validation.stepsRequired"));
      assert(
        wholeNumberRegex.test(targetSteps),
        400,
        i18n.t("common:validation.stepsMustBeNumber")
      );
    }
    if (goalType === "sleep") {
      assert(targetSleep, 400, i18n.t("common:validation.sleepTimeRequired"));
      assert(
        wholeNumberRegex.test(targetSleep),
        400,
        i18n.t("common:validation.sleepMustBeNumber")
      );
    }
    if (goalType === "waterIntake") {
      assert(
        targetWaterIntake != null && targetWaterIntake !== "",
        400,
        "Water intake goal is required"
      );
      const num = Number(targetWaterIntake);
      assert(
        !Number.isNaN(num) && num > 0,
        400,
        "Water intake must be a positive number"
      );
    }
    if (goalType === "custom") {
      validateCustomGoal(customName, customTarget, customIcon, assert);
    }
    if (goalType === "personalRecord") {
      validatePersonalRecordGoal(targetName, targetLoad, targetReps, assert);
    }

    return true;
  } catch (error) {
    flash("error", getErrorMessage(error));
    submit("/actions/refresh");
    return false;
  }
};

const validateCustomGoal = (customName, customTarget, customIcon, assert) => {
  assert(customName, 400, i18n.t("common:validation.goalNameRequired"));
  assert(customTarget, 400, i18n.t("common:validation.goalTargetRequired"));
  assert(customIcon, 400, i18n.t("common:validation.goalIconRequired"));
  assert(
    customName.trim().length > 3,
    400,
    i18n.t("common:validation.goalNameMinLength")
  );
  assert(
    customTarget.trim().length > 2,
    400,
    i18n.t("common:validation.goalTargetMinLength")
  );
};

const validatePersonalRecordGoal = (
  targetName,
  targetLoad,
  targetReps,
  assert
) => {
  assert(targetName, 400, i18n.t("common:validation.exerciseNameRequired"));
  assert(targetLoad, 400, i18n.t("common:validation.targetLoadRequired"));
  assert(
    loadRegex.test(targetLoad),
    400,
    i18n.t("common:validation.targetLoadMustBeNumber")
  );
  assert(targetReps, 400, i18n.t("common:validation.targetRepsRequired"));
  assert(
    wholeNumberRegex.test(targetReps),
    400,
    i18n.t("common:validation.targetRepsMustBeNumber")
  );
};
